<template>
  <div>
    <div v-if="!working">
      <b-alert variant="info" :show="shippingsWithErrors.length > 0">
        <p v-translate="{count: shippingsWithErrors.length}">FYI, there are %{count} shippings with errors (address, status, already in a tour and/or not in routing zone) that won't be routed by this tool.</p>
        <b-button size="sm" @click.prevent="copyToClipboard(shippingsWithErrors.join('\n'))" v-translate>Copy IDs to clipboard</b-button>
      </b-alert>
      <b-card>
          <b-row>
            <b-col cols="3">
              <translate>Total Shippings Fetched</translate>
              <h1 class="m-0">{{total}}</h1>
            </b-col>
            <b-col cols="3">
              <translate>Shippings by Tour</translate>
              <b-input class="h1" v-model="divider" size="xl" type="number" />
            </b-col>
            <b-col cols="3">
              <translate>Total suggested Tours</translate>
              <h1 class="m-0">{{toRouteTotal}}</h1>
            </b-col>
            <b-col cols="3">
              <translate>Total to Route</translate>
              <h1 class="m-0">{{ Object.values(toRoute).reduce((a, b) => a + b, 0)}}</h1>
            </b-col>
            <b-col cols="12">
              <hr>
            </b-col>
            <b-col cols="6">
              <b-button size="sm" variant="warning" @click.prevent="setShippingGroups" :disabled="nothingToDo" v-translate>Reset Groups</b-button>
              <b-button size="sm" @click.prevent="mergeSelectedGroups" :disabled="selected.length < 2" v-translate>Merge Selected</b-button>
              <b-button size="sm" variant="danger" @click.prevent="deleteSelectedGroups" :disabled="selected.length === 0" v-translate>Delete Selected</b-button>
            </b-col>
            <b-col cols="6" class="text-right align-self-center">
              <b-form-checkbox size="sm" switch class="d-inline-block mr-1" style="line-height: 1" :disabled="nothingToDo" v-model="allAsWaiting"><translate>Generate ALL Tours as WAITING</translate></b-form-checkbox>
              <b-button size="sm" variant="success" @click.prevent="createTours" :disabled="nothingToDo" v-translate>Generate All Tours</b-button>
            </b-col>
            <b-col cols="12">
              <hr>
            </b-col>
          </b-row>
          <b-row v-for="(group, index) in shippingGroups" :key="index">
            <b-col><b-form-checkbox v-model="selected" :value="{[index]: group}">{{ index }}</b-form-checkbox></b-col>
            <b-col>
              <span :id="'tooltip-target-' + index">{{ group.length }}</span>
              <b-tooltip :target="'tooltip-target-' + index" triggers="hover">
                <div v-for="(item, index) in clusterShippingSizes(group)" :key="index">
                  <b-badge class="mr-1" variant="dark">{{index}}</b-badge>
                  <span
                    v-translate="{count: item}"
                    :translate-n="item"
                    translate-plural="%{ count } shippings"
                  >%{ count } shipping
                  </span>
                </div>
                <p class="m-0 mt-2">{{clusterShippingsWeight(group)}} KG.</p>
              </b-tooltip>
            </b-col>
            <b-col>
              <b-input-group>
                <b-input size="sm" type="number" number v-model="toRoute[index]" class="mr-1" />
                <b-button size="sm" :variant="assignButtonBg(index)" v-b-modal.modalAssignCouriers @click="assigningGroup = index" :disabled="asWaiting[index] || allAsWaiting">{{ toAssign[index] ? toAssign[index].length : 0 }} <font-awesome-icon icon="user" /></b-button>
              </b-input-group>
            </b-col>
            <b-col><b-select size="sm" v-if="clusteringMethod[index]" v-model="clusteringMethod[index]" :options="clusteringMethodSelect"></b-select></b-col>
            <b-col>
              <b-button size="sm" @click="viewGroupMap(index)"><font-awesome-icon :icon="['far', 'eye']" /></b-button>
              <b-form-checkbox class="d-inline-block ml-1" style="line-height: 1" switch size="sm" v-model="asWaiting[index]" :disabled="allAsWaiting || toAssign[index] && toAssign[index].length > 0"><translate>as WAITING</translate></b-form-checkbox>
            </b-col>
            <b-col cols="12"><hr class="my-2"></b-col>
          </b-row>
      </b-card>
    </div>
    <b-card v-else>
      <b-row v-if="!posting">
        <b-col class="text-center mt-5">
          <h2>
            <b-spinner  />
            <translate>Fetching Shippings, please wait...</translate>
          </h2>
          <h3 v-show="total !== 0" v-translate="{count: count, total}">%{count}/%{total}</h3>
          <h3 v-show="total === 0" v-translate>Calculating total shippings to fetch...</h3>
          <h4 v-show="this.remainingTime !== 0" v-translate="{remainingTime: this.remainingTime}">Ending %{remainingTime}</h4>
          <h4 v-show="this.remainingTime === 0" v-translate>Calculating remaining time...</h4>
        </b-col>
      </b-row>
      <b-row v-else-if="posting && !success">
        <b-col class="text-center mt-5">
          <h2>
            <b-spinner  />
            <translate>Creating Tours, please wait...</translate>
          </h2>
          <h3 v-show="total !== 0" v-translate="{count: count, total}">%{count}/%{total}</h3>
          <h3 v-show="total === 0" v-translate>Calculating total shippings to fetch...</h3>
          <h4 v-show="this.remainingTime !== 0" v-translate="{remainingTime: this.remainingTime}">Ending %{remainingTime}</h4>
          <h4 v-show="this.remainingTime === 0" v-translate>Calculating remaining time...</h4>
        </b-col>
      </b-row>
      <b-row v-else-if="success">
        <b-col class="text-center mt-5">
          <h1>
            <translate>All Tours have been created! 😊</translate>
          </h1>
          <h2 v-show="total !== 0" v-translate="{total}">%{total}/%{total}</h2>
          <div class="m-auto w-50 text-left">
            <ul>
              <li v-for="id in createdTourIds"><a :href="'/edit/tours/' + id" target="_blank">{{id}}</a></li>
            </ul>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-modal id="modalClustersMap" size="xl" ref="modalClustersMap" @shown="showPairs = true; addMarkers()" @hide="showPairs = false; clusteringMethodReady = false; routingMethod = null" hide-header hide-footer>
      <div slot="modal-title" v-translate>Group Map</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <leaflet-map ref="fullMap" style="height:80vh" fitAlways :geolocate="false" @itemRemoved="removeFromGroup">
        <template #controls-bottomleft>
          <div style="width: 200px;" v-if="!$cookies.get('bypassCountry')">
            <b-card>
              <b-form-checkbox v-model="showAreas" :value="true" :unchecked-value="false">
                <translate>Routing Zones</translate>
              </b-form-checkbox>
              <b-form-checkbox v-model="showAreasTooltips" :value="true" :unchecked-value="false" :disabled="!showAreas">
                <translate>Routing Zones' Names</translate>
              </b-form-checkbox>
            </b-card>
          </div>
        </template>
      </leaflet-map>
    </b-modal>

    <b-modal id="modalAssignCouriers" size="xl" ref="modalAssignCouriers" hide-footer @hidden="assigningGroup = null">
      <div slot="modal-title" v-translate="{assigningGroup: assigningGroup}">Assign Couriers for %{assigningGroup} group</div>
      <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
      <h4 v-translate="{needed: toRoute[assigningGroup], fulfilled: toAssign[assigningGroup] ? toAssign[assigningGroup].length : 0}">Needed: %{needed}. Fulfilled: %{fulfilled}</h4>
      <div class="form-group" v-if="!toAssign[assigningGroup] || toAssign[assigningGroup].length < toRoute[assigningGroup]">
        <user-autocomplete ref="courierAc" placeholder="Courier Name or Email" @selected="addCourierToGroup" :restrictToRoles="['courier']" with-picture :disabled="toAssign[assigningGroup] && toAssign[assigningGroup].length === toRoute[assigningGroup]" />
      </div>
      <b-card v-for="(user, index) in toAssign[assigningGroup]" :key="index">
        <span class="bg-danger text-white d-block text-center" style="cursor: pointer; width: 1rem; height: 1rem; line-height: 1rem; position: absolute; top: 0; left: 0" @click.prevent="removeCourierFromGroup(index)">×</span>
        <user-card :user="user" profilePictureSize="50px" :withTitles="false" :withMap="false"></user-card>
      </b-card>
    </b-modal>
  </div>
</template>

<script>
import LeafletMap from '@/components/LeafletMap'
import UserAutocomplete from '@/components/autocompletes/UserAutocomplete'
import UserCard from '@/components/UserCard'

export default {
  name: 'ShippingMegaRouting',
  components: {
    LeafletMap,
    UserAutocomplete,
    UserCard
  },
  props: {
    filters: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      working: false,
      posting: false,
      success: false,
      fetchChunkSize: 300,
      postChunkSize: 10,
      counter: 0,
      estimatedChunks: 0,
      remainingTime: 0,
      timeDiff: [],
      shippings: [],
      shippingGroups: {},
      shippingsWithErrors: [],
      selected: [],
      divider: 25,
      toRoute: {},
      toRouteTotal: 0,
      asWaiting: {},
      count: 0,
      total: 0,
      clusteringMethod: {},
      clusteringMethodSelect: [
        {
          text: this.$gettext('Best Tours'),
          value: 'cluster_vehicle_quantity'
        },
        {
          text: this.$gettext('Equally distributed'),
          value: 'cluster_vehicle_capacity'
        }
      ],
      clusterColors: [
        'FF6C87',
        '8A89E1',
        'C5E662',
        '4DA2FF',
        'FFB54D',
        'F03A47',
        '1B998B',
        '6d05d6',
        '000000',
        'C5D86D',
        '712F79',
        'F7996E',
        '48639C',
        'CA895F',
        '4E3822',
        '06BEE1',
        'ff6600',
        '319200',
        '653300',
        '884cff'
      ],
      markers: [],
      routingMethod: null,
      selectedGroup: [],
      selectedClusters: [],
      showAreas: false,
      showAreasTooltips: false,
      selectedKey: null,
      vehicleMaxCapacity: 5,
      vehicleMaxCapacityWeight: null,
      allAsWaiting: false,
      toAssign: {},
      assigningGroup: null,
      createdTourIds: []
    }
  },
  computed: {
    nothingToDo() {
      return Object.keys(this.shippingGroups).length === 0
    }
  },
  watch: {
    divider(val) {
      if (val && val !== 0) {
        this.toRouteTotal = 0
        for (const key in this.shippingGroups) {
          this.$set(this.toRoute, key, Math.ceil(this.shippingGroups[key].length / this.divider))
          this.toRouteTotal += this.toRoute[key]
          if (parseInt(this.toRoute[key]) > 1) {
            if (!this.clusteringMethod[key]) {
              this.$set(this.clusteringMethod, key, 'cluster_vehicle_quantity')
            }
          } else {
            delete this.clusteringMethod[key]
          }
        }
      }
    },
    // shippingGroups: {
    //   deep: true,
    //   handler(val, oldVal) {
    //     console.log(oldVal, val)
    //     if (Object.keys(oldVal).length === 0) {
    //       this.toRouteTotal = 0
    //       for (const key in val) {
    //         this.setToRouteQuantity(key)
    //         this.toRouteTotal += this.toRoute[key]
    //       }
    //     }
    //   }
    // },
    toRoute: {
      deep: true,
      handler(val, oldVal) {
        for (const key in val) {
          if (this.shippingGroups[key] && this.shippingGroups[key].length < val[key]) {
            this.$nextTick(() => {
              this.toRoute[key] = this.shippingGroups[key].length
            })
          }
          if (val[key] > 1) {
            if (!this.clusteringMethod[key]) {
              this.$set(this.clusteringMethod, key, 'cluster_vehicle_quantity')
            }
          } else {
            delete this.clusteringMethod[key]
          }
        }
      }
    },
    showAreas(val) {
      if (val) {
        this.$refs.fullMap.showAreas = true
      } else {
        this.$refs.fullMap.showAreas = false
      }
      this.$cookies.set('showAreas', val)
    },
    showAreasTooltips(val) {
      if (val) {
        this.$refs.fullMap.showAreasTooltips = true
      } else {
        this.$refs.fullMap.showAreasTooltips = false
        this.showAreas = false
        this.$nextTick(() => {
          this.showAreas = true
        })
      }
      this.$cookies.set('showAreasTooltips', val)
    },
    allAsWaiting(val) {
      if (val) {
        if (confirm(this.$gettext('Are your sure you want to Generate ALL Tours as WAITING?\n\nNOTE: This will DELETE any Courier previously assigned to any Group.'))) {
          this.toAssign = {}
        } else {
          this.$nextTick(() => {
            this.allAsWaiting = false
          })
        }
      }
    }
  },
  created () {
    this.fetchShippingSizes()
  },
  mounted() {
    delete this.filters.format
    delete this.filters.includes
    this.filters.limit = this.fetchChunkSize
    this.filters.page = 0
    this.filters.includes = ['last_known_place']
    this.filters.fields = 'id,meta,address_from,address_to,last_known_place,routing_zone.name,size_id,status,tour_id'
    this.getShippings()
  },
  methods: {
    async getShippings() {
      this.timeDiff = []
      this.working = true
      await this.getChunk(this.filters)
        .then(() => {
          this.working = false
          this.total = this.setShippingGroups()
        })
    },
    async getChunk(params, prevResults = []) {
      const startTime = new Date()
      let chunkResults = []
      return await this.$http.get(this.$appConfig.api.baseUrl + 'shippings', {params})
        .then(async response => {
          chunkResults = prevResults.concat(response.body.data)
          const endTime = new Date()
          this.timeDiff.push(endTime - startTime)
          this.total = response.body.pagination.total
          this.estimatedChunks = Math.ceil(this.total / this.fetchChunkSize)
          this.remainingTime = this.moment().add(((this.estimatedChunks - params.page) * (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length)), 'milliseconds').fromNow()
          this.animateValue(chunkResults.length, (chunkResults.length + this.fetchChunkSize) <= this.total ? chunkResults.length + this.fetchChunkSize : this.total, (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length))
          if (response.body.data.length !== 0) {
            params.page = params.page + 1
            await this.getChunk(params, chunkResults)
          } else {
            this.total = chunkResults.length
            this.shippings = chunkResults
          }
        }, async () => {
          this.$root.$children[0].$refs.simplert.justCloseSimplert()
          await this.getChunk(params, prevResults)
        })
    },
    async createTours() {
      if (confirm(this.$gettext('Are your sure you want to GENERATE ALL TOURS?\nHave you reviewed everything TWICE?\nSURE??\n\n***WARNING***\nThis action CANNOT BE UNDONE!'))) {
        console.log(this.shippingGroups)
        this.posting = true
        this.working = true
        this.createdTourIds = []
        this.timeDiff = []
        let toPost = []
        for (const key in this.shippingGroups) {
          if (Object.hasOwnProperty.call(this.shippingGroups, key)) {
            const shippings = this.shippingGroups[key]
            if (this.toRoute[key] === 1) {
              toPost.push(
                {
                  key: key,
                  assignTo: this.toAssign[key] && this.toAssign[key][0] ? this.toAssign[key][0].id : false,
                  asWaiting: this.asWaiting[key] || this.allAsWaiting,
                  payload: {
                    shippings: shippings.map(x => x.id.replace('suggest_', '')),
                    start_location: {
                      lat: shippings[0].pickup.lat,
                      lng: shippings[0].pickup.lng
                    },
                    scheduled_date: null,
                    couriersCount: 1
                  }
                }
              )
            } else if (this.toRoute[key] > 1) {
              this.selectedKey = key
              let clusters = await this.sendToSuggest(shippings, true)
              clusters.forEach((cluster, i) => {
                toPost.push(
                  {
                    key: key,
                    assignTo: this.toAssign[key] && this.toAssign[key][i] ? this.toAssign[key][i].id : false,
                    asWaiting: this.asWaiting[key] || this.allAsWaiting,
                    payload: {
                      shippings: cluster,
                      start_location: {
                        lat: shippings.find(id => cluster[0][0]).pickup.lat,
                        lng: shippings.find(id => cluster[0][0]).pickup.lng
                      },
                      scheduled_date: null,
                      couriersCount: 1
                    }
                  }
                )
              })
            }
          }
        }
        this.remainingTime = 0
        this.total = toPost.length
        this.count = 0
        let chunks = Array.from({ length: Math.ceil(toPost.length / this.postChunkSize) }, (v, i) =>
          toPost.slice(i * this.postChunkSize, i * this.postChunkSize + this.postChunkSize)
        )
        for (let i = 0; i < chunks.length; i++) {
          let startTime = new Date()
          const element = chunks[i]
          await this.postChunk(element)
            .then(() => {
              let endTime = new Date()
              this.timeDiff.push(endTime - startTime)
              this.estimatedChunks = Math.ceil(this.total / this.postChunkSize)
              this.remainingTime = this.moment().add(((this.estimatedChunks - this.counter) * (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length)), 'milliseconds').fromNow()
              this.animateValue(this.counter * this.postChunkSize, ((this.counter + 1) * this.postChunkSize) < this.total ? (this.counter + 1) * this.postChunkSize : this.total, (this.timeDiff.reduce((a, b) => a + b, 0) / this.timeDiff.length))
              this.counter++
            }, e => {
              console.log('ERROR in createTours()', e)
            })
        }
        this.success = true
      }
    },
    async postChunk(chunks) {
      let toPost = []
      let toRePost = []
      for (let i = 0; i < chunks.length; i++) {
        toPost.push(
          this.$http.post(this.$appConfig.api.baseUrl + 'tours', chunks[i].payload)
            .then(response => {
              this.createdTourIds.push(response.body[0].id)

              if (chunks[i].asWaiting) {
                this.$http.post(this.$appConfig.api.baseUrl + 'tours/' + response.body[0].id + '/waiting')
              } else if (chunks[i].assignTo) {
                this.$http.patch(this.$appConfig.api.baseUrl + 'tours/' + response.body[0].id, {courier_id: chunks[i].assignTo})
              }
              return response
            }, e => {
              toRePost.push(chunks[i])
              console.log('ERROR in postChunk() loop', e)
              this.$root.$children[0].$refs.simplert.justCloseSimplert()
            })
        )
      }
      return await Promise.all(toPost)
        .then(responses => {
          if (toRePost.length === 0) {
            return responses
          } else this.postChunk(toRePost)
        }, e => {
          console.log('ERROR in postChunk() Promise', e)
        })
    },
    animateValue(start, end, duration) {
      let startTimestamp = null
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp
        const progress = Math.min((timestamp - startTimestamp) / duration, 1)
        this.count = Math.floor(progress * (end - start) + start)
        if (progress < 1) {
          window.requestAnimationFrame(step)
        }
      }
      window.requestAnimationFrame(step)
    },
    setShippingGroups() {
      let shippingCount = 0
      this.toRoute = {}
      this.toRouteTotal = 0
      this.shippingsWithErrors = []
      this.shippingGroups = this.sortByKey(
        this.groupBy(this.shippings.map(
          shipping => {
            if (shipping.address_to && shipping.routing_zone && shipping.status === 'READY' && !shipping.tour_id) {
              shippingCount++
              return this.transformShippingToRoutifierShipping(shipping)
            } else {
              this.shippingsWithErrors.push(shipping.id)
            }
          }
        ).filter(Boolean), 'routingZoneName')
      )
      for (const key in this.shippingGroups) {
        this.setToRouteQuantity(key)
        this.toRouteTotal += this.toRoute[key]
      }
      this.countryAreas = this.fetchCountryAreas()
      return shippingCount
    },
    transformShippingToRoutifierShipping(shipping) {
      const weight = shipping.meta && shipping.meta.items && shipping.meta.items.length > 1 ? shipping.meta.items.reduce((a, b) => {
        return a + parseInt(b.weight || (b.item ? b.item.weight || 0 : null))
      }, 0) : (shipping.meta && shipping.meta.items && shipping.meta.items.length === 1 ? parseInt(shipping.meta.items[0] ? shipping.meta.items[0].weight : 0) : null)

      const formatted = {
        id: 'suggest_' + shipping.id,
        pickup: {
          id: (shipping.last_known_place.id || shipping.address_from.id) + '',
          lat: (shipping.last_known_place.lat || shipping.address_from.lat),
          lng: (shipping.last_known_place.lng || shipping.address_from.lng)
        },
        dropoff: {
          id: shipping.address_to.id + '',
          lat: shipping.address_to.lat,
          lng: shipping.address_to.lng
        },
        package_size: this.shippingSizes.find(x => x.id === shipping.size_id).name,
        package_weight: weight,
        routingZoneName: shipping.routing_zone ? shipping.routing_zone.name : 'missssstery 👻'
      }

      return formatted
    },
    groupBy(xs, key) {
      return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x)
        return rv
      }, {})
    },
    sortByKey(obj) {
      return Object.fromEntries(Object.entries(obj).sort())
    },
    mergeSelectedGroups() {
      const newGroupKey = this.selected.map(x => Object.keys(x)).sort().join(' + ')
      let newGroupValues = []
      this.selected.forEach((x, i) => {
        newGroupValues = newGroupValues.concat(...Object.values(x))
        Object.keys(x).forEach(key => {
          delete this.shippingGroups[key]
          delete this.toRoute[key]
          delete this.clusteringMethod[key]
          if (this.countryAreas.findIndex(x => x.text.indexOf(key) !== -1) !== -1) {
            this.countryAreas[this.countryAreas.findIndex(x => x.text.indexOf(key) !== -1)].text = this.countryAreas[this.countryAreas.findIndex(x => x.text.indexOf(key) !== -1)].text.replace(/\(\d+\)/, '(mixed)')
          }
        })
      })

      this.shippingGroups[newGroupKey] = newGroupValues
      this.setToRouteQuantity(newGroupKey)

      this.shippingGroups = this.sortByKey(this.shippingGroups)
      this.selected = []
    },
    deleteSelectedGroups() {
      this.selected.forEach((x, i) => {
        Object.keys(x).forEach(key => {
          delete this.shippingGroups[key]
          delete this.toRoute[key]
          delete this.clusteringMethod[key]
        })
      })
      this.shippingGroups = this.sortByKey(this.shippingGroups)
      this.selected = []
    },
    setToRouteQuantity(key) {
      this.$set(this.toRoute, key, Math.ceil(this.shippingGroups[key].length / this.divider))
      if (parseInt(this.toRoute[key]) > 1) {
        if (!this.clusteringMethod[key]) {
          this.$set(this.clusteringMethod, key, 'cluster_vehicle_quantity')
        }
      } else {
        delete this.clusteringMethod[key]
      }
    },
    removeFromGroup(id) {
      this.shippingGroups[this.selectedKey].splice(this.shippingGroups[this.selectedKey].findIndex(x => x.id === 'suggest_' + id), 1)
      this.viewGroupMap(this.selectedKey)
    },
    async viewGroupMap(key) {
      this.selectedKey = key
      if (this.toRoute[key] === 1) {
        this.selectedGroup = this.simulateRouterResponse(this.shippingGroups[key])
      } else {
        await this.sendToSuggest(this.shippingGroups[key])
      }
      this.$refs.modalClustersMap.show()
      this.updateClustersMarkers()
    },
    simulateRouterResponse(group) {
      let newGroup = []
      for (const shipping of group) {
        if (newGroup.findIndex(x => x.type === 'pickup' && x.location_id === shipping.pickup.id) === -1) {
          newGroup.push({
            shipping_id: shipping.id,
            type: 'pickup',
            location_id: shipping.pickup.id,
            coordinates: {
              lat: shipping.pickup.lat,
              lng: shipping.pickup.lng
            },
            package_size: shipping.package_size,
            cluster: 1
          })
        }

        if (newGroup.findIndex(x => x.type === 'dropoff' && x.location_id === shipping.dropoff.id) === -1) {
          newGroup.push({
            shipping_id: shipping.id,
            type: 'dropoff',
            location_id: shipping.dropoff.id,
            coordinates: {
              lat: shipping.dropoff.lat,
              lng: shipping.dropoff.lng
            },
            package_size: shipping.package_size,
            cluster: 1
          })
        }
      }
      return [newGroup]
    },
    async sendToSuggest(shippings, toRoutifier = false) {
      if (shippings.length > 1) {
        // Routifier complains about not expected routingZoneName field
        shippings = shippings.map(x => {
          delete x.routingZoneName
          return x
        })

        let payload = {
          shippings: shippings,
          vehicles: [{
            id: 'vehicle_0',
            start_at: {
              lat: shippings[0].pickup.lat,
              lng: shippings[0].pickup.lng
            },
            vehicle_max_capacity: Math.ceil(shippings.length / this.toRoute[this.selectedKey]),
            vehicle_max_capacity_weight: this.vehicleMaxCapacityWeight * 1000
          }],
          'min_vehicles:packages': 1
        }

        if (this.$frontConfig.clustering && this.$frontConfig.clustering[this.clusteringMethod]) {
          payload.routing_method = this.$frontConfig.clustering[this.clusteringMethod]
        } else if (this.routingMethod) {
          payload.routing_method = this.routingMethod
        }

        if (this.clusteringMethod[this.selectedKey] === 'cluster_vehicle_quantity') {
          for (let i = 0; i < this.toRoute[this.selectedKey] - 1; i++) {
            payload.vehicles.push({
              id: 'vehicle_' + (i + 1),
              start_at: {
                lat: shippings[0].pickup.lat,
                lng: shippings[0].pickup.lng
              },
              vehicle_max_capacity: Math.ceil(shippings.length / this.toRoute[this.selectedKey]),
              vehicle_max_capacity_weight: this.vehicleMaxCapacityWeight * 1000
            })
          }
        }

        return await this.$http.post(this.$appConfig.routerApi.baseUrl + this.clusteringMethod[this.selectedKey], payload)
          .then(async response => {
            if (!toRoutifier) {
              this.selectedGroup = response.data.solution.map((cluster, n) => {
                let pickups = []
                let dropoffs = []
                cluster.tour.forEach((item, i) => {
                  let pickupData = item.type !== 'pickup' ? shippings.find(x => x.id === item.shipping_id).pickup : null
                  if (pickupData) {
                    if (pickups.findIndex(x => x.location_id === pickupData.id) === -1) {
                      pickups.push({
                        cluster: item.cluster,
                        coordinates: {
                          lat: pickupData.lat,
                          lng: pickupData.lng
                        },
                        location_id: pickupData.id,
                        shipping_id: item.shipping_id,
                        type: 'pickup',
                        package_size: item.package_size,
                        package_weight: item.package_weight
                      })
                    }
                  }
                  let dropoffData = item.type !== 'dropoff' ? shippings.find(x => x.id === item.shipping_id).dropoff : null
                  if (dropoffData) {
                    if (dropoffs.findIndex(x => x.location_id === dropoffData.id) === -1) {
                      dropoffs.push({
                        cluster: item.cluster,
                        coordinates: {
                          lat: dropoffData.lat,
                          lng: dropoffData.lng
                        },
                        location_id: dropoffData.id,
                        shipping_id: item.shipping_id,
                        type: 'dropoff',
                        package_size: item.package_size,
                        package_weight: item.package_weight
                      })
                    }
                  }
                })
                if (n >= this.clusterColors.length) {
                  this.clusterColors.push(this.stringToColour((cluster.tour.length * 2343) + (cluster.tour[0].cluster * -13) + cluster.vehicles_id + cluster.tour[0].shipping_id + (cluster.tour[0].cluster * -12) + 'asd' + (cluster.tour[0].cluster * 24) + 'dsa').replace('#', ''))
                }
                return cluster.tour.concat(pickups).concat(dropoffs)
              })

              this.updateClustersMarkers()
            } else {
              let toReturn = []
              response.data.solution.forEach(cluster => {
                toReturn.push([...new Set(cluster.tour.map(x => x.shipping_id.replace('suggest_', '')))])
              })
              return toReturn
            }
          })
      }
    },
    updateClustersMarkers() {
      this.selectedClusters = []
      this.markers = []
      let infoWindowHtml = ''
      let id = 0
      this.selectedGroup.forEach((cluster, i) => {
        let iconDropoff = {
          type: 'map-marker',
          size: [27, 36],
          anchor: [13.5, 36],
          color: '#' + this.clusterColors[i],
          text: '↓'
        }
        let iconPickup = {
          type: 'map-marker',
          size: [27, 36],
          anchor: [13.5, 36],
          color: this.pSBC(-0.6, '#' + this.clusterColors[i]),
          text: '↑'
        }
        cluster.forEach((item, n) => {
          id++
          let shipping = this.shippings.find(x => 'suggest_' + x.id === item.shipping_id)
          let infoText
          infoWindowHtml = '<p>ID: <strong>' + shipping.id + '</strong></p>'
          infoWindowHtml += '<p>From: <strong>' + shipping.address_from.formatted_address + '</strong></p>'
          infoWindowHtml += '<p>To: <strong>' + shipping.address_to.formatted_address + '</strong>'
          infoWindowHtml += shipping.routing_zone && shipping.routing_zone.name ? ' (' + shipping.routing_zone.name + ')</p>' : ''
          infoWindowHtml += item.type === 'dropoff' ? '<p>Cluster: <strong>' + (i + 1) + '</strong></p>' : ''

          infoText = item.type === 'dropoff' ? {id: item.shipping_id.replace('suggest_', ''), type: item.type === 'dropoff' ? 'megaRoutingCluster' : 'shipping', cluster: i, html: infoWindowHtml} : this.$gettextInterpolate(this.$gettext('Cluster %{number} Origin'), {number: (i + 1)})

          this.markers.push({
            id: id,
            lat: item.coordinates.lat,
            lng: item.coordinates.lng,
            icon: item.type === 'dropoff' ? iconDropoff : iconPickup,
            x: 11,
            y: 34,
            infoText: infoText,
            popup: item.type === 'dropoff',
            tooltip: item.type === 'pickup'
          })
        })
      })

      this.addMarkers()
    },
    addMarkers(ref = 'fullMap') {
      if (this.$refs[ref]) {
        this.$refs[ref].markers = []
        setTimeout(function () {
          if (this.$refs[ref]) {
            this.checkShowAreas(ref)
            this.$refs[ref].addMarkers(this.markers)
          }
        }.bind(this), 50)
      }
    },
    checkShowAreas(ref) {
      // console.log(this.countryAreas)
      this.$refs[ref].areas = this.countryAreas
      this.$refs.fullMap.showAreas = this.showAreas = this.$cookies.get('showAreas') === 'true'
      this.$refs.fullMap.showAreasTooltips = this.showAreasTooltips = this.$cookies.get('showAreasTooltips') === 'true'
    },
    async fetchCountryAreas() {
      let params = {}

      if (window.country !== null && !this.$cookies.get('bypassCountry')) {
        params = {
          filter_groups: [{
            filters: [
              ['country_iso_code', 'eq', window.country]
            ]
          }]
        }
        return await this.$http.get(this.$appConfig.api.baseUrl + 'routing-zones', {params: params})
          .then(response => {
            this.countryAreas = response.body.data.map(area => {
              return {
                text: area.name + (this.shippingGroups[area.name] && this.shippingGroups[area.name].length > 0 ? ` (${this.shippingGroups[area.name].length})` : ' (0)'),
                value: {
                  coords: JSON.parse(JSON.stringify(area.area)).coordinates.map(linearRing =>
                    linearRing.slice(0, linearRing.length - 1)
                      .map(([lng, lat]) => ({lat, lng}))
                  )
                }
              }
            }).filter(Boolean)
            return this.countryAreas
          })
      } else {
        return []
      }
    },
    clusterShippingSizes(shippings) {
      return shippings.map(x => {
        return x.package_size
      }).filter(Boolean).reduce(function (counter, item) {
        let p = (String)(item)
        counter[p] = counter.hasOwnProperty(p) ? counter[p] + 1 : 1
        return counter
      }, {})
    },
    clusterShippingsWeight(shippings) {
      return (Math.round(((shippings.reduce((a, b) => {
        return a + (b.package_weight || 0)
      }, 0) / 1000) + Number.EPSILON) * 100) / 100).toFixed(2)
    },
    assignButtonBg(key) {
      if (!this.toAssign[key] || this.toAssign[key].length === 0) {
        return 'dark'
      } else if (this.toAssign[key].length < this.toRoute[key]) {
        return 'warning'
      } else {
        return 'success'
      }
    },
    addCourierToGroup(user) {
      if (!this.toAssign[this.assigningGroup]) {
        this.$set(this.toAssign, this.assigningGroup, [])
      }
      this.toAssign[this.assigningGroup].push(user)
    },
    removeCourierFromGroup(index) {
      this.toAssign[this.assigningGroup].splice(index, 1)
    }
  }
}
</script>
